/**
 * The Fourth Trimester: range switching and header dropdowns.
 *
 * 1. Range tabs (homepage). The active range lives in html[data-range]; the
 *    CSS in tft-styles.css ("Range tabs") does the showing and hiding, so
 *    this script only maintains that attribute, the tab ARIA state and the
 *    URL hash. Deep links: /#postpartum, /#cycle, /#wellness. Links anywhere
 *    on the page can switch ranges with data-range-switch="<key>".
 * 2. Header dropdowns (Shop / Guides / About): hover and focus open them via
 *    CSS; this adds click-to-toggle for touch, Escape and outside-click to
 *    close, and keeps aria-expanded honest.
 */
(function () {
  'use strict';

  var RANGES = ['postpartum', 'cycle', 'wellness'];
  var root = document.documentElement;

  function currentRange() {
    var h = (location.hash || '').slice(1);
    return RANGES.indexOf(h) !== -1 ? h : null;
  }

  // ---- Range tabs --------------------------------------------------------
  var bar = document.querySelector('[data-range-tabs]');
  var tabs = bar ? Array.prototype.slice.call(bar.querySelectorAll('[role="tab"]')) : [];

  function applyRange(key, opts) {
    opts = opts || {};
    if (RANGES.indexOf(key) === -1) key = 'postpartum';
    // The hero above the bar changes height when its panel swaps, which
    // would shove the bar (and the reader) up or down the page. Measure the
    // bar before and after and cancel the difference, so the bar stays
    // exactly where it was on screen.
    // Exception: a switch triggered from ABOVE the bar (the hero's "New"
    // pill) comes from the very block that changes height, so anchoring the
    // bar would scroll the reader; there we keep the scroll position as is.
    // Measured in document coordinates and restored with scrollTo, so any
    // adjustment the browser makes on its own during the swap is overridden
    // rather than added to. The hero panels share one height (.hero-stack),
    // so in practice the bar does not move and the page stays exactly put.
    var anchorBar = bar && opts.anchor !== false;
    var y0 = window.scrollY;
    var before = bar ? bar.getBoundingClientRect().top + y0 : null;
    root.setAttribute('data-range', key);
    if (bar) {
      var after = bar.getBoundingClientRect().top + window.scrollY;
      var target = anchorBar ? y0 + (after - before) : y0;
      if (window.scrollY !== target) window.scrollTo({ top: target, left: 0, behavior: 'instant' });
    }
    tabs.forEach(function (t) {
      var on = t.getAttribute('data-category') === key;
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.tabIndex = on ? 0 : -1;
    });
    if (opts.updateHash !== false && bar) {
      // replaceState rather than setting location.hash: no scroll jump, no
      // history entry per tab press.
      if (history.replaceState) history.replaceState(null, '', '#' + key);
    }
    // Deliberately no scrolling on a tab change: the content swaps in place
    // under the bar and the hero above it swaps too, so there is nowhere
    // better to be than where the reader already is.
    if (opts.focus) {
      var active = tabs.filter(function (t) { return t.getAttribute('data-category') === key; })[0];
      if (active) active.focus();
    }
  }

  if (bar) {
    // The inline head script already set data-range from the hash before
    // first paint; this just syncs the tab state to it.
    applyRange(root.getAttribute('data-range') || currentRange() || 'postpartum', { updateHash: false });

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function (e) {
        e.preventDefault();
        applyRange(tab.getAttribute('data-category'));
      });
      tab.addEventListener('keydown', function (e) {
        var i = tabs.indexOf(tab);
        var next = null;
        if (e.key === 'ArrowRight') next = tabs[(i + 1) % tabs.length];
        if (e.key === 'ArrowLeft') next = tabs[(i - 1 + tabs.length) % tabs.length];
        if (e.key === 'Home') next = tabs[0];
        if (e.key === 'End') next = tabs[tabs.length - 1];
        if (next) {
          e.preventDefault();
          applyRange(next.getAttribute('data-category'), { focus: true });
        }
      });
    });

    window.addEventListener('hashchange', function () {
      var r = currentRange();
      if (r) applyRange(r, { updateHash: false });
    });
  }

  // Range-switch links (hero pill, header dropdown on the homepage, cards)
  document.addEventListener('click', function (e) {
    var link = e.target.closest ? e.target.closest('[data-range-switch]') : null;
    if (!link) return;
    var key = link.getAttribute('data-range-switch');
    if (!bar) return; // not on the homepage: let the href (/#key) navigate
    e.preventDefault();
    var aboveBar = link.getBoundingClientRect().top < bar.getBoundingClientRect().top;
    applyRange(key, { anchor: !aboveBar });
  });

  // ---- Header dropdowns --------------------------------------------------
  var items = Array.prototype.slice.call(document.querySelectorAll('.nav-item--dropdown'));

  function closeAll(except) {
    items.forEach(function (item) {
      if (item === except) return;
      item.classList.remove('is-open');
      var btn = item.querySelector('.nav-link--toggle');
      if (btn) btn.setAttribute('aria-expanded', 'false');
    });
  }

  items.forEach(function (item) {
    var btn = item.querySelector('.nav-link--toggle');
    if (!btn) return;
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      var open = item.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      closeAll(item);
    });
    item.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        item.classList.remove('is-open');
        btn.setAttribute('aria-expanded', 'false');
        btn.focus();
      }
    });
  });

  document.addEventListener('click', function (e) {
    if (!e.target.closest || !e.target.closest('.nav-item--dropdown')) closeAll(null);
  });
})();
